import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Save,
  RotateCcw,
  User,
  Zap,
  Image,
  Loader2,
  CheckCircle,
  ChevronDown
} from 'lucide-react';
import { getStyleOptions, getStyle } from '../config/aiStyles';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuthStore } from '../stores/authStore';
import ProviderSelection from '../components/ProviderSelection';
import { Link } from 'react-router-dom';

const Settings = () => {
  const { user, getFlickrAccount } = useAuthStore();
  const flickrAccount = getFlickrAccount();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [preferences, setPreferences] = useState({
    defaultStyle: 'artistic', // Default to most popular
    descriptionSentences: 3,
    enablePreview: true
  });
  
  const styleOptions = getStyleOptions();

  useEffect(() => {
    loadPreferences();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStyleDropdown && !event.target.closest('.style-dropdown')) {
        setShowStyleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStyleDropdown]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users/preferences', {
        withCredentials: true
      });
      setPreferences(response.data.preferences);
    } catch (error) {
      toast.error('Failed to load preferences');
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put('/api/users/preferences', preferences, {
        withCredentials: true
      });
      toast.success('Preferences saved successfully!');
      
      // Show the saved message
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 3000); // Hide after 3 seconds
      
    } catch (error) {
      toast.error('Failed to save preferences');
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    if (!window.confirm('Are you sure you want to reset all preferences to their default values?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('/api/users/preferences/reset', {}, {
        withCredentials: true
      });
      setPreferences(response.data.preferences);
      toast.success('Preferences reset to defaults');
      
      // Show the saved message
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 3000); // Hide after 3 seconds
      
    } catch (error) {
      toast.error('Failed to reset preferences');
      console.error('Error resetting preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <SettingsIcon className="mr-3" />
          Settings
        </h1>
        <p className="text-gray-600 mt-1">
          Configure your AI metadata generation preferences
        </p>
      </div>

      {/* Connected Accounts Section */}
      <ProviderSelection
        user={user}
        title="Connected Accounts"
        subtitle="Manage your photo platform connections"
        showExisting={true}
        className="mb-6"
      />


      {/* AI Generation Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Zap className="mr-2 h-5 w-5" />
            AI Generation Settings
          </h2>
        </div>
        <div className="px-6 py-4 space-y-6">
          {/* Default Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Style
            </label>
            <div className="relative style-dropdown">
              <button
                type="button"
                onClick={() => setShowStyleDropdown(!showStyleDropdown)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{getStyle(preferences.defaultStyle).shortTitle}</span>
                    <span className="text-gray-500 ml-2">— {getStyle(preferences.defaultStyle).description}</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showStyleDropdown ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {showStyleDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
                  {styleOptions.map(option => {
                    const style = getStyle(option.value);
                    return (
                      <div
                        key={option.value}
                        onClick={() => {
                          updatePreference('defaultStyle', option.value);
                          setShowStyleDropdown(false);
                        }}
                        className={`cursor-pointer px-3 py-3 hover:bg-gray-50 border-l-4 transition-colors ${
                          preferences.defaultStyle === option.value 
                            ? `${style.selectedBorderColor} bg-blue-50` 
                            : 'border-transparent'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-3 h-3 rounded-full mt-1 ${style.selectedBorderColor.replace('border-', 'bg-')}`}></div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {style.shortTitle}
                              {style.popular && (
                                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                  Most Popular
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">{style.description}</div>
                            <div className="text-xs text-gray-500 mt-1">Best for: {style.bestFor}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              This will be the default style for new jobs
            </p>
          </div>

          {/* Description Sentences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description Length: {preferences.descriptionSentences} {preferences.descriptionSentences === 1 ? 'sentence' : 'sentences'}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={preferences.descriptionSentences}
              onChange={(e) => updatePreference('descriptionSentences', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1</span>
              <span>3</span>
              <span>5</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Number of sentences in generated descriptions
            </p>
          </div>

          {/* Enable Preview */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">Enable Preview</label>
              <p className="text-xs text-gray-500">Preview generated metadata before applying</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.enablePreview}
              onChange={(e) => updatePreference('enablePreview', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>
      </div>



      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <button
          onClick={resetPreferences}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </button>

        <div className="flex items-center space-x-3">
          {/* Saved Message */}
          {showSavedMessage && (
            <div className="flex items-center text-green-600 text-sm font-medium transition-all duration-300 ease-in-out animate-pulse">
              <CheckCircle className="mr-1 h-4 w-4" />
              Settings saved!
            </div>
          )}
          
          {/* Save Button */}
          <button
            onClick={savePreferences}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
